import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  declineRequest,
  grantRequest,
  listRequestsToDecide,
  type DecidableRequest,
  type RequestOffer,
} from "../../../api/access/accessRequestsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { toUiError } from "../../../errors/uiError";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import DropdownChevron from "../../../ui/overlays/DropdownChevron";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import MenuButton from "../../../ui/overlays/MenuButton";
import { needText } from "../../../ui/feedback/refusalWords";
import type { GroupNamer } from "../model/groupNames";

interface RequestsToDecideProps {
  /** The project, or `null` for installation permissions (Administration › Accounts). */
  projectId: string | null;
  /** Names the offered groups as the scope's list does. */
  namer: GroupNamer;
  /** A grant put someone into a group: the list of groups and people reads again. */
  onGranted: () => void;
}

/**
 * The waiting requests one may grant, on top of Project › Access and Administration › Accounts
 * (accounts.md GS-8, redesign R3-3 `access`): who asks for what and why; granting offers the group
 * that adds least first, with what it adds, the broader ones on request; or decline. Nothing
 * shows while none waits.
 */
export default function RequestsToDecide({ projectId, namer, onGranted }: RequestsToDecideProps) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listRequestsToDecide(projectId, signal), [projectId]);
  const { state, reload, refresh } = useLoad(load);
  if (state.status === "failed") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} compact={true} />;
  if (state.status !== "loaded" || state.value.length === 0) return null;
  return (
    <ul aria-label={t("access.requests.title")} className="space-y-2">
      {state.value.map((decidable) => (
        <RequestRow key={decidable.request.requestId} decidable={decidable} projectId={projectId} namer={namer}
          onDecided={(granted) => { refresh(); if (granted) onGranted(); }} />
      ))}
    </ul>
  );
}

interface RequestRowProps {
  decidable: DecidableRequest;
  projectId: string | null;
  namer: GroupNamer;
  onDecided: (granted: boolean) => void;
}

function RequestRow({ decidable, projectId, namer, onDecided }: RequestRowProps) {
  const { t, i18n } = useTranslation();
  const { state, submit } = useSubmission();
  const pending = state.status === "submitting";
  const { request, requesterName, offers } = decidable;
  const [least, ...broader] = offers;
  // What joining a group gives here, beyond the request: "adds Propose changes and Releases".
  const adds = (offer: RequestOffer) => t("access.requests.adds", {
    grants: new Intl.ListFormat(i18n.resolvedLanguage, { type: "conjunction" }).format(
      offer.adds.filter((added) => added.projectId === projectId).map((added) => needText(t, added.needs))),
  });
  const grant = (offer: RequestOffer) => {
    submit(async () => {
      try {
        await grantRequest(request.requestId, offer.groupId);
      } catch (caught) {
        // The person was put in, but the request ended meanwhile (409 with `joined_group`): granted all the same.
        const error = toUiError(caught);
        if (error.kind !== "problem" || typeof error.problem.details["joined_group"] !== "string") throw caught;
      }
      onDecided(true);
    });
  };
  // Groups by the list's names, DMW's own in the reader's language (Sol critique C2).
  const groupName = (offer: RequestOffer) => namer.name(offer.groupId, offer.name);
  return (
    // The request on its own line, then what granting adds and the actions, at every width and in
    // every language, so a long request or a long group name never squeezes it (Sol critique C1).
    <li className="app-card grid gap-2 px-4 py-2 text-sm">
      <p className="min-w-0">
        <span className="font-semibold">{requesterName}</span>
        {` ${t("access.requests.asks", { permission: needText(t, request.needs) })}`}
        {request.reason === "" ? null : <span className="app-text-muted">{` · ${t("access.requests.reason", { reason: request.reason })}`}</span>}
      </p>
      <div className="flex flex-wrap items-center gap-2">
      {least === undefined ? null : (
        <>
          <span className="app-text-muted text-xs">{adds(least)}</span>
          <AppButton variant="primary" size="sm" disabled={pending} onClick={() => { grant(least); }}>{t("access.addTo", { group: groupName(least) })}</AppButton>
        </>
      )}
      {broader.length === 0 ? null : (
        <MenuButton
          align="right"
          width="lg"
          sections={[{ id: "broader", items: broader.map((offer) => ({
            id: offer.groupId,
            label: `${t("access.addTo", { group: groupName(offer) })} · ${adds(offer)}`,
            action: { kind: "select", onSelect: () => { grant(offer); } },
          })) }]}
          renderTrigger={(trigger, open) => (
            <AppButton {...trigger} variant="secondary" size="sm" disabled={pending}>
              {t("access.requests.otherGroups")}
              <DropdownChevron open={open} />
            </AppButton>
          )}
        />
      )}
      <AppButton variant="secondary" size="sm" disabled={pending}
        onClick={() => { submit(async () => { await declineRequest(request.requestId); onDecided(false); }); }}>
        {t("access.requests.decline")}
      </AppButton>
      </div>
      {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
    </li>
  );
}
